import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import { type CampaignDetail, fetchCampaign } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { campaignQuery, meQuery } from '../lib/queries.js';

// DRAFTING screen — new-campaign handoff §4.3. Centered Mag (casting) +
// honest ETA + a live progress checklist that reads straight off the
// server. Polls every 1.5s; when the campaign flips to 'ready', kicks the
// founder to REVIEW. Failed → inline retry-back-to-brief.

const POLL_INTERVAL_MS = 1500;

export const Route = createFileRoute('/brand/$slug/campaigns/new/drafting/$id')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const campaign = await queryClient.ensureQueryData(campaignQuery(params.id));
    if (!campaign) throw redirect({ to: '/brands' });
    // Still in the intake chat? Bounce back to the brief screen with
    // the campaignId so the conversation resumes where it left off.
    if (campaign.status === 'intake') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/brief',
        params: { slug: params.slug },
        search: { campaignId: campaign.id },
      });
    }
    // Only conversion_path is still pipeline-less. analytics_setup has
    // its own draft worker as of s121, so it's fine on the drafting
    // screen — the checklist labels are kind-aware server-side.
    if (campaign.kind === 'conversion_path') {
      throw redirect({
        to: '/brand/$slug/campaigns',
        params: { slug: params.slug },
      });
    }
    // Already done? Jump them straight to where they belong.
    if (campaign.status === 'ready') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/review/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    if (campaign.status === 'launched') {
      throw redirect({
        to: '/brand/$slug/campaigns/new/launched/$id',
        params: { slug: params.slug, id: campaign.id },
      });
    }
    return { me, campaign };
  },
  loader: ({ context }) => context as { me: unknown; campaign: CampaignDetail },
  component: DraftingPage,
});

function DraftingPage() {
  const navigate = useNavigate();
  const { campaign: initial } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const [campaign, setCampaign] = useState<CampaignDetail>(initial);
  const navigated = useRef(false);
  const { t } = useLingui();
  useBrandTitle(slug, [campaign.name ? t`${campaign.name} · drafting` : t`drafting`]);

  // Poll the campaign while status is 'drafting'. The dep array keeps
  // only `campaign.id` so a status change doesn't tear down the interval
  // mid-tick — the second effect below handles the navigation when the
  // poll's setCampaign lands on 'ready'.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await fetchCampaign(campaign.id);
        if (cancelled || !next) return;
        setCampaign(next);
      } catch (err) {
        console.error('[drafting] poll failed', err);
      }
    };
    void tick(); // kick once on mount so we don't wait POLL_INTERVAL_MS
    const id = setInterval(tick, POLL_INTERVAL_MS);
    // Re-poll immediately when the tab regains focus — covers the case
    // where setInterval starves while backgrounded.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [campaign.id]);

  // Status-watcher. Whichever path lands a terminal status first
  // (initial loader, polling tick, or a returning visibilitychange
  // refetch), this effect navigates exactly once. Refactored out of
  // the polling tick because the previous in-tick navigation occasionally
  // missed the flip when React's effect deps cycled mid-update.
  useEffect(() => {
    if (navigated.current) return;
    if (campaign.status === 'ready') {
      navigated.current = true;
      navigate({
        to: '/brand/$slug/campaigns/new/review/$id',
        params: { slug, id: campaign.id },
      });
    } else if (campaign.status === 'failed') {
      navigated.current = true;
      magToast.error(
        t`mag couldn't finish the draft.`,
        campaign.failureReason ?? t`try again from the brief.`,
      );
    }
  }, [campaign.status, campaign.id, campaign.failureReason, navigate, slug, t]);

  const steps = campaign.progress?.steps ?? [];
  const etaSeconds = campaign.progress?.etaSeconds ?? 45;
  const seconds = Math.max(5, etaSeconds);

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="campaigns" />
      <header className="flex h-12 items-center justify-between border-b border-[var(--line)] bg-[var(--cream)] px-4 sm:px-7">
        <Link
          to="/brand/$slug/campaigns"
          params={{ slug }}
          className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
        >
          {'←'} <Trans>back to campaigns</Trans>
        </Link>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>NEW CAMPAIGN</Trans>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--spell)] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--pulse)] mag-pulse" />
            <Trans>MAG IS DRAFTING</Trans>
          </span>
        </div>
      </header>

      <section className="mx-auto flex max-w-[520px] flex-col items-center px-4 pb-16 pt-16 sm:px-7 text-center">
        <Mag size={140} mood="casting" />
        <p className="mt-6 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>MAG IS WORKING · ~{seconds} SECONDS</Trans>
        </p>
        <p className="mt-3 font-serif text-[28px] leading-[1.15] text-[var(--ink)]">
          <Trans>
            “ok. reading your past campaigns, looking at your customer section, drafting some
            creative.”
          </Trans>
        </p>

        {campaign.status === 'failed' ? (
          <FailureBlock reason={campaign.failureReason} slug={slug} />
        ) : (
          <div className="mt-8 w-full text-left">
            {steps.map((s) => (
              <ChecklistRow key={s.id} label={s.label} state={s.state} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function ChecklistRow({ label, state }: { label: string; state: 'pending' | 'active' | 'done' }) {
  const { t } = useLingui();
  return (
    <div className="flex items-center gap-3 py-2">
      <span
        className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border-[1.5px] ${
          state === 'done'
            ? 'border-[var(--ink)] bg-[var(--ink)] text-[var(--spell)]'
            : state === 'active'
              ? 'border-[var(--ink)] bg-white text-[var(--ink)]'
              : 'border-[var(--ink)]/30 bg-white text-[var(--ink)]/40'
        }`}
        aria-hidden
      >
        {state === 'done' ? (
          <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
            <title>{t`done`}</title>
            <path
              d="M2 6.5l2.5 2.5L10 3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : state === 'active' ? (
          <span className="font-mono text-[11px]">·</span>
        ) : null}
      </span>
      <span
        className={`text-[14px] leading-[1.4] ${
          state === 'pending'
            ? 'text-[var(--ink)]/40'
            : state === 'active'
              ? 'font-bold text-[var(--ink)]'
              : 'text-[var(--ink)]/85'
        }`}
      >
        {label}
      </span>
      {state === 'active' ? (
        <span className="font-mono text-[14px] text-[var(--plum)]">…</span>
      ) : null}
    </div>
  );
}

function FailureBlock({ reason, slug }: { reason: string | null; slug: string }) {
  const { t } = useLingui();
  return (
    <div className="mt-8 w-full rounded-xl border-[1.5px] border-[var(--pulse)] bg-white p-5 text-left">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--pulse)]">
        {'↘'} <Trans>draft failed</Trans>
      </p>
      <p className="mt-2 font-serif text-[16px] leading-tight text-[var(--ink)]">
        <Trans>“{reason ?? t`something tripped me up.`}”</Trans>
      </p>
      <div className="mt-3 flex justify-end">
        <Link to="/brand/$slug/campaigns/new/brief" params={{ slug }} className="btn btn-ghost">
          <Trans>try again</Trans>
          <span className="btn-arrow">→</span>
        </Link>
      </div>
    </div>
  );
}
